import { useMemo } from 'react'

import { getMarkerFootnoteNumberBySourceKey } from '@/agent-v2/references/agent-v2-citation-labels'
import { agentV2SourceKey } from '@/agent-v2/references/agent-v2-source-key'
import { selectAgentV2ReferencesByCitationId } from '@/agent-v2/references/store/selectors'
import { selectAgentV2TranscriptMessages } from '@/agent-v2/store/selectors'
import type { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'

/**
 * The footnote number a turn's message shows for a cited source, so the content viewer can label the
 * source the way the message does ("[4] Halikoytakis v. Future Motion, Inc."). Null when the message
 * shows none.
 */
export function useAgentV2CitationFootnoteNumber(props: {
  conversationId: string | undefined
  turnId: string | undefined
  citationId: string | undefined
}): number | null {
  const { conversationId, turnId, citationId } = props
  const messages = useAppSelector((state: RootState) => selectAgentV2TranscriptMessages(state, { conversationId }))
  const referencesByCitationId = useAppSelector((state: RootState) => selectAgentV2ReferencesByCitationId(state, { conversationId: conversationId ?? '' }))

  // Both of a turn's messages carry its id, and only the assistant's text cites anything. Read the text
  // out here so numbering is rebuilt when that text changes, not on every delta of an unrelated turn.
  const messageText = messages.find((candidate) => candidate.role === 'assistant' && candidate.turnId === turnId)?.text

  return useMemo(() => {
    // A turn missing from the transcript has no text to derive numbering from.
    const reference = citationId ? referencesByCitationId[citationId] : undefined
    if (!reference || messageText === undefined) return null

    return getMarkerFootnoteNumberBySourceKey(messageText, referencesByCitationId)[agentV2SourceKey(reference)] ?? null
  }, [messageText, referencesByCitationId, citationId])
}
